// @givmo/compliance — the notice for when nobody gets deadline reminders (CCM-R1-REMIND-WEB;
// D-CCM-18).
//
// ══ NEVER SILENTLY OFF ═════════════════════════════════════════════════════════════════════════
//
// Reminders go to the organization's Compliance Owners and Global Admins who have them on. When
// nobody is left — each switched theirs off, or none can be reached — the Calendar says so plainly,
// under its header, because it is about the whole page (the approved reference render, 2026-09-29:
// "Calendar only"). The profile
// read's `reminder_recipients_present` is the ONE input, from the same read the reminders are sent
// by: a served `false` renders the notice; `true`, `null` (the backend could not make that read) and
// absent render NOTHING, so an answer this build cannot read never claims that nobody is reminded.
// There is no dismiss. It goes away once someone turns theirs back on, and the host re-reads the
// profile when the viewer's own switch is written.
//
// ══ WHICH SECOND LINE ══════════════════════════════════════════════════════════════════════════
//
// The switch is served to exactly the people reminders go to, so the host's answer to "is the
// viewer served it" (`reminders.switchServed`) is the Owner-or-Member question. Served: "Turn yours
// on in your notification settings", a link to the host's Notifications card. Anyone else, and every
// host without a Settings page: "Ask one of them to turn theirs on." The notice waits for that answer
// rather than drawing one line and swapping it for the other.
//
// ⚠ Law item 5: the notice states no date and no result, so the not-a-substitute sentence does not
// attach to it. ⚠ No red, no pill, no button and no urgency words: the design system's flat card.
//
// DATA CONTRACT: the `compliance-reminders-notice` test-id family.

import { useQuery } from '@tanstack/react-query';
import { Card, Icon, shouldInterceptClick, testIdProp } from '@givmo/design-system';
import { BellOff } from 'lucide-react';
import { decodeProfile } from './compliance-decode';
import { useCompliance } from './compliance-context';
import { complianceProfileQueryKey } from './compliance-queries';

/** The viewer's own switch, as the Compliance module declares it in the preferences registry. */
export const COMPLIANCE_REMINDERS_PREFERENCE_KEY = 'compliance.reminders.email';

/**
 * Whether the profile says nobody gets deadline reminders: a served `false` only (`true`, `null` and
 * absent are not that answer). The notice speaks only then, and a host reads the viewer's own
 * preferences only then (the REMIND-WEB review P1: lazily, never on every page), so the two cannot
 * disagree about when the switch matters.
 */
export function nobodyGetsReminders(profileBody: unknown): boolean {
  return decodeProfile(profileBody)?.reminderRecipientsPresent === false;
}

const PREFIX = 'compliance-reminders-notice';

export function RemindersNotice() {
  const { orgRef, isReady, operations, moduleStanding, reminders } = useCompliance();
  const profile = useQuery({
    queryKey: complianceProfileQueryKey(orgRef),
    queryFn: () => operations.getProfile(orgRef ?? ''),
    enabled: isReady && moduleStanding === 'available' && orgRef !== null,
  });

  if (!nobodyGetsReminders(profile.data)) return null;
  const served = reminders === undefined ? false : reminders.switchServed;
  if (served === undefined) return null;
  const door = served ? reminders?.settings : undefined;

  return (
    // The flat card: white on the page's sunken ground, a hairline and no shadow, so it reads as a
    // message about the page rather than one more item in its lists.
    <Card flush className="flex items-start gap-12 px-16 py-14" testId={PREFIX}>
      <Icon icon={BellOff} size={18} className="mt-1 shrink-0 text-icon" aria-hidden />
      <div className="flex min-w-0 flex-col gap-2">
        <p className="text-s14 font-w600 text-text-strong">
          Nobody in your organization gets deadline reminders by email.
        </p>
        <p className="text-s13 leading-h15 text-pretty text-text-secondary">
          Reminders go to Compliance Owners and Global Admins who have them on.{' '}
          {door !== undefined ? (
            <>
              Turn yours on in{' '}
              <a
                href={door.href}
                onClick={(event) => {
                  if (!shouldInterceptClick(event)) return;
                  event.preventDefault();
                  door.open();
                }}
                className="whitespace-nowrap underline underline-offset-2"
                {...testIdProp(`${PREFIX}-settings`)}
              >
                your notification settings
              </a>
              .
            </>
          ) : (
            'Ask one of them to turn theirs on.'
          )}
        </p>
      </div>
    </Card>
  );
}
